import { defineStore } from "pinia";
import { ref } from "vue"
import * as z from 'zod'
import { generateTimestamp, isNewerThan } from '~/utils/timestamp'

type Plan = {
  planName: string;
  interval: string;
  currency: string;
  unitAmt: string;
  subQty: string;
  subtotalAmt: string;
  monthly_fee_product_id: string;
  ram: number;
  disk: number;
  vcpus: number;
  monthly_fee: string;
  location: string;
  hourly_fee: string;
  hourly_fee_product_id: string;
  original_monthly_fee?: string | null;
  original_hourly_fee?: string | null;
  product_id: number;
  metaData: string;
};

export type OrderItems = Plan[];

interface CartSyncData {
  cart: Plan[];
  interval: string | undefined;
  lastModified: string;
}

interface ServerCartResponse {
  content: {
    cart: Plan[];
    interval: string;
    lastModified?: string;
  };
}

type Stock = {
  planName: string;
  ram: number;
  disk: number;
  vcpus: number;
  switch: string;
  busway?: string; 
  monthly_fee: string;
  location: string;
  hourly_fee: string;
  monthly_fee_product_id: string;
  hourly_fee_product_id: string;
  original_monthly_fee: string | null;
  original_hourly_fee: string | null;
  // interval: string;
  currency: string;
  unitAmt: string;
  left: number;
  stock_quantity: number;
  product_id: number;
  // stock: number;
  gpu_type:string;
};

type StockIssue = {
  productId: number;
  planName: string;
  requestedQuantity: number;
  availableQuantity: number;
  reason: 'out_of_stock' | 'insufficient_stock' | 'product_unavailable' | 'price_unavailable';
};
type DataList = {
  disk: number;
  hourly_fee: string;
  hourly_fee_product_id: string;
  id: number;
  location: string;
  monthly_fee: string;
  monthly_fee_product_id: string;
  name: string;
  ram: number;
  stock_quantity: number;
  vcpus: number;
};

type FetchResult = {
  result: boolean;
  content: any;
  message?: string;
};

// Zod 驗證函數
const isValidStock = (item: any): item is Stock => {
  const result = StockSchema.safeParse(item);
  if (!result.success) {
    console.warn('Stock validation failed:', {
      data: item,
      errors: result.error.flatten().fieldErrors
    });
  }
  return result.success;
};

const isValidPlan = (item: any): item is Plan => {
  const result = PlanSchema.safeParse(item);
  if (!result.success) {
    console.warn('Plan validation failed:', {
      data: item,
      errors: result.error.flatten().fieldErrors
    });
  }
  return result.success;
};

type ApiFlavorItem = {
  name: string;
  ram: number;
  disk: number;
  vcpus: number;
  switch: string;
  busway: string;
  monthly_fee: string;
  location: string;
  hourly_fee: string;
  monthly_fee_product_id: string;
  hourly_fee_product_id: string;
  original_monthly_fee: string | null;
  original_hourly_fee: string | null;
  stock_quantity: number;
  product_id: number;
  gpu_type: string;
};

// Zod Schemas
const PlanSchema = z.object({
  planName: z.string(),
  interval: z.string(),
  currency: z.string(),
  unitAmt: z.string(),
  subQty: z.string(),
  subtotalAmt: z.string(),
  monthly_fee_product_id: z.string(),
  ram: z.number(),
  disk: z.number(),
  vcpus: z.number(),
  monthly_fee: z.string(),
  location: z.string(),
  hourly_fee: z.string(),
  hourly_fee_product_id: z.string(),
  original_monthly_fee: z.string().optional(),
  original_hourly_fee: z.string().optional(),
  product_id: z.number(),
  metaData: z.string(),
});

const StockSchema = z.object({
  planName: z.string(),
  ram: z.number(),
  disk: z.number(),
  vcpus: z.number().optional(),
  switch: z.string().optional(),
  busway: z.string().optional(), 
  monthly_fee: z.string(),
  location: z.string(),
  hourly_fee: z.string(),
  monthly_fee_product_id: z.string(),
  hourly_fee_product_id: z.string(),
  original_monthly_fee: z.string().nullable(),
  original_hourly_fee: z.string().nullable(),
  currency: z.string(),
  unitAmt: z.string(),
  left: z.number(),
  stock_quantity: z.number(),
  product_id: z.number(),
  gpu_type: z.string(),
});

const ApiFlavorItemSchema = z.object({
  name: z.string(),
  ram: z.number(),
  disk: z.number(),
  vcpus: z.number(),
  switch: z.string(),
  busway: z.string(),
  monthly_fee: z.string(),
  location: z.string(),
  hourly_fee: z.string(),
  monthly_fee_product_id: z.string(),
  hourly_fee_product_id: z.string(),
  original_monthly_fee: z.string().nullable(),
  original_hourly_fee: z.string().nullable(),
  stock_quantity: z.number(),
  product_id: z.number(),
  gpu_type: z.string(),
});

// 深度比較函數
function deepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;
  
  if (obj1 == null || obj2 == null) return obj1 === obj2;
  
  if (typeof obj1 !== typeof obj2) return false;
  
  if (typeof obj1 !== 'object') return obj1 === obj2;
  
  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;
  
  if (Array.isArray(obj1)) {
    if (obj1.length !== obj2.length) return false;
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) return false;
    }
    return true;
  }
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }
  
  return true;
}

export const useCartState = defineStore(
  "cart",
  () => {
    // current cart
    const config = useRuntimeConfig();
    const orderItems: Ref<OrderItems> = ref([]);
    // current stock
    const stock: Ref<Stock[]> = ref([]);
    const orderInterval = ref<string>("");
    // Add timestamp for synchronization
    const lastModified = ref<string>(generateTimestamp());
    
    // Sync state
    let syncTimer: NodeJS.Timeout | null = null;
    let isSyncing = false;

    // Helper function to update timestamp
    const updateTimestamp = (): void => {
      lastModified.value = generateTimestamp();
    };

    // Debounced sync to server
    const debouncedSyncToServer = () => {
      if (!import.meta.client) return;
      
      // Don't sync during logout process to prevent empty cart from being synced
      const { isProcessingSignOut } = useAuthHandler();
      if (isProcessingSignOut.value) {
        console.log('Skipping cart sync during logout process');
        return;
      }
      
      // Clear existing timer
      if (syncTimer) {
        clearTimeout(syncTimer);
      }
      
      // Set new timer
      syncTimer = setTimeout(async () => {
        if (isSyncing) return;
        
        try {
          isSyncing = true;
          await $fetch('/api/proxy/api/v3/gsn/cart/items', {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              data: {
                cart: orderItems.value,
                interval: orderInterval.value,
                lastModified: lastModified.value,
              },
            }),
          });
        } catch (error) {
          console.error("Error syncing cart to server:", error);
        } finally {
          isSyncing = false;
        }
      }, 500); // 500ms 防抖延遲
    };

    const resetStock = () => {
      stock.value = [];
    };

    const getStock = async (token?: string) => {
      try {        
        const data = await $fetch<FetchResult>(
          `${config.public.APIurl}/api/v3/gsn/flavors`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        resetStock();
        // Log to verify server-side execution during SSR
        
        if (!data.result || !data.content) {
          throw new Error(data.message || "Failed to fetch stock data");
        }
        
        (data.content as ApiFlavorItem[]).forEach((item: ApiFlavorItem) => {
          const tmpData: Stock = {
            planName: item.name,
            ram: item.ram,
            disk: item.disk,
            vcpus: item.vcpus,
            switch: item.switch ?? null,
            busway: item.busway ?? null,
            monthly_fee: item.monthly_fee,
            location: item.location,
            hourly_fee: item.hourly_fee,
            monthly_fee_product_id: item.monthly_fee_product_id,
            hourly_fee_product_id: item.hourly_fee_product_id,
            original_monthly_fee: item.original_monthly_fee,
            original_hourly_fee: item.original_hourly_fee,
            left: item.stock_quantity,
            stock_quantity: item.stock_quantity,
            unitAmt: "",
            currency: "USD",
            product_id: item.product_id,
            gpu_type: item.gpu_type,
          };
          stock.value.push(tmpData);
        });

        return stock.value;
      } catch (error) {
        console.error("Error fetching stock:", error);
        resetStock(); // Clear stock on error
        throw error;
      }
    };

    const checkStock = (id: string, qty: string) => {
      const orderItem = orderItems.value.find(
        (item) => item.product_id.toString() === id
      );
      const stockItem = stock.value.find((item) => item.product_id.toString() === id);
      const currentQuantity = Number(orderItem?.subQty ?? 0);
      const requestedQuantity = Number(qty);
      const availableQuantity = Number(stockItem?.stock_quantity ?? stockItem?.left ?? 0);
      const price = orderInterval.value === 'month' ? stockItem?.monthly_fee : stockItem?.hourly_fee;
      if (price === '?') return false;
      if (currentQuantity + requestedQuantity > availableQuantity) {
        return false;
      }
      return true;
    };

    const getStockIssues = (): StockIssue[] => {
      return orderItems.value.flatMap((item): StockIssue[] => {
        const stockItem = stock.value.find(
          (stockProduct) => stockProduct.product_id === item.product_id
        );
        const requestedQuantity = Number(item.subQty);

        const price = item.interval === 'month' ? item.monthly_fee : item.hourly_fee;
        if (price === '?') {
          return [{
            productId: item.product_id,
            planName: item.planName,
            requestedQuantity,
            availableQuantity: 0,
            reason: 'price_unavailable' as const,
          }];
        }

        if (!stockItem) {
          return [{
            productId: item.product_id,
            planName: item.planName,
            requestedQuantity,
            availableQuantity: 0,
            reason: 'product_unavailable' as const,
          }];
        }

        const availableQuantity = Number(stockItem.stock_quantity ?? stockItem.left ?? 0);
        if (requestedQuantity > availableQuantity) {
          return [{
            productId: item.product_id,
            planName: item.planName,
            requestedQuantity,
            availableQuantity,
            reason: availableQuantity === 0 ? 'out_of_stock' as const : 'insufficient_stock' as const,
          }];
        }

        return [];
      });
    };

    const addToCart = (id: string, planType: string, amount: number = 1) => {
      if (
        planType === "" ||
        (orderInterval.value !== "" && planType !== orderInterval.value)
      ) {
        console.error("planType is not match");
        return;
      }
      orderInterval.value = planType;
      const plan: Plan | undefined = orderItems.value.find(
        (item) => item.product_id.toString() === id
      );
      const stockItem = stock.value.find((item) => item.product_id.toString() === id);
      if (!stockItem || !checkStock(id, amount.toString())) {
        console.warn(`Insufficient stock for product ${id}`);
        return;
      }
      if (plan) {
        const qty = Number(plan.subQty) + amount;
        const subtotal = Number(plan.subtotalAmt) + (Number(plan.unitAmt) * amount);
        plan.subQty = qty.toString();
        plan.subtotalAmt = subtotal.toFixed(2).toString();

        // Update timestamp and sync to server after successful operation
        updateTimestamp();
        debouncedSyncToServer();
        return;
      } else {
        const product = stock.value.find(
          (productItem) => productItem.product_id.toString() === id
        );
        if (!product) return;

        orderItems.value.push({
          product_id: product.product_id,
          planName: product.planName,
          interval: orderInterval.value,
          monthly_fee_product_id: product.monthly_fee_product_id,
          hourly_fee: product.hourly_fee,
          hourly_fee_product_id: product.hourly_fee_product_id,
          original_monthly_fee: product.original_monthly_fee,
          original_hourly_fee: product.original_hourly_fee,
          ram: product.ram,
          disk: product.disk,
          vcpus: product.vcpus,
          monthly_fee: product.monthly_fee,
          location: product.location,
          metaData:
            orderInterval.value === "month"
              ? product.monthly_fee_product_id
              : product.hourly_fee_product_id,
          currency: "USD",
          unitAmt:
            orderInterval.value === "month"
              ? product.monthly_fee.toString()
              : product.hourly_fee.toString(),
          subQty: amount.toString(),
          subtotalAmt:
            orderInterval.value === "month"
              ? (Number(product.monthly_fee) * amount).toFixed(2).toString()
              : (Number(product.hourly_fee) * amount).toFixed(2).toString(),
        });
        
        // Update timestamp and sync to server after successful operation
        updateTimestamp();
        debouncedSyncToServer();
      }
    };

    const removeFromCart = (id: string) => {
      const plan = orderItems.value.find((item) => item.product_id.toString() === id);
      if (plan && Number(plan.subQty) > 0) {
        const qty = Number(plan.subQty) - 1;
        const subtotal = Number(plan.subtotalAmt) - Number(plan.unitAmt);

        plan.subQty = qty.toString();
        plan.subtotalAmt = subtotal.toFixed(2).toString();
        if (Number(plan.subQty) === 0) {
          orderItems.value = orderItems.value.filter(
            (item) => item.product_id.toString() !== id
          );
        }
        
        // Update timestamp and sync to server after successful operation
        updateTimestamp();
        debouncedSyncToServer();
      }

      if (orderItems.value.length === 0) {
        orderInterval.value = "";
        // Update timestamp and sync to server when cart becomes empty
        updateTimestamp();
        debouncedSyncToServer();
      }
    };

    const clearCart = () => {
      orderItems.value = [];
      orderInterval.value = "";
      
      // Update timestamp and sync to server after clearing cart
      updateTimestamp();
      debouncedSyncToServer();
    };

    const emptyData = () => {
      orderItems.value = [];
      stock.value = [];
      orderInterval.value = "";
      
      // Clear localStorage to ensure clean state for next user
      if (import.meta.client) {
        localStorage.removeItem('cart');
      }
      
      // Reset timestamp but don't trigger server sync during logout
      // This prevents empty cart state from being synced to server
      lastModified.value = generateTimestamp();
    };

    /**
     * Synchronizes the shopping cart with current stock data to prevent server errors.
     *
     * This function validates cart items against available stock and ensures data consistency:
     * - Removes items from cart if they no longer exist in stock
     * - Removes items if the current plan type pricing is null (not available for sale)
     * - (Currently disabled) Adjusts quantities that exceed available stock
     * - Prevents submission of invalid products or quantities to the backend
     *
     * @returns void - Updates orderItems.value in place
     */
    const syncCartWithStock = () => {
      // 如果没有库存数据，不要执行同步，避免清空购物车
      if (stock.value.length === 0) {
        return;
      }
      
      const updatedOrderItems: OrderItems = [];
      
      orderItems.value.forEach((item) => {
        // 只要有 product_id 就尝试匹配库存
        if (!item.product_id) {
          return;
        }

        // 查找对应的库存商品
        const stockItem = stock.value.find((stock) => {
          return stock.product_id === item.product_id;
        });

        if (!stockItem) {
          // 只有真正找不到对应库存时才移除
          console.warn(`Product ${item.product_id} (${item.planName}) not found in stock, removing from cart`);
          return;
        }

        // 檢查當前 planType 對應的費用是否為 null
        const currentPlanType = orderInterval.value;
        if (currentPlanType === 'month' && (stockItem.monthly_fee === null || stockItem.monthly_fee === 'null')) {
          console.warn(`Product ${item.product_id} (${item.planName}) monthly fee is null, removing from cart`);
          return;
        }
        
        if (currentPlanType === 'hour' && (stockItem.hourly_fee === null || stockItem.hourly_fee === 'null')) {
          console.warn(`Product ${item.product_id} (${item.planName}) hourly fee is null, removing from cart`);
          return;
        }

        // 找到对应库存且費用有效，將 stock 資料更新到購物車項目
        const updatedItem = { ...item };
        
        // 從 stock 更新所有相關資料到購物車
        updatedItem.planName = stockItem.planName;
        updatedItem.ram = stockItem.ram;
        updatedItem.disk = stockItem.disk;
        updatedItem.vcpus = stockItem.vcpus;
        updatedItem.location = stockItem.location;
        updatedItem.monthly_fee = stockItem.monthly_fee;
        updatedItem.hourly_fee = stockItem.hourly_fee;
        updatedItem.monthly_fee_product_id = stockItem.monthly_fee_product_id;
        updatedItem.hourly_fee_product_id = stockItem.hourly_fee_product_id;
        
        // 更新原始價格（如果存在）
        if (stockItem.original_monthly_fee !== undefined) {
          updatedItem.original_monthly_fee = stockItem.original_monthly_fee;
        }
        if (stockItem.original_hourly_fee !== undefined) {
          updatedItem.original_hourly_fee = stockItem.original_hourly_fee;
        }
        
        // 根據當前計費類型更新 unitAmt、metaData 和 subtotalAmt
        if (orderInterval.value === 'month') {
          updatedItem.unitAmt = stockItem.monthly_fee;
          updatedItem.metaData = stockItem.monthly_fee_product_id;
          updatedItem.subtotalAmt = (Number(stockItem.monthly_fee) * Number(item.subQty)).toFixed(2).toString();
        } else if (orderInterval.value === 'hour') {
          updatedItem.unitAmt = stockItem.hourly_fee;
          updatedItem.metaData = stockItem.hourly_fee_product_id;
          updatedItem.subtotalAmt = (Number(stockItem.hourly_fee) * Number(item.subQty)).toFixed(2).toString();
        }
        
        updatedOrderItems.push(updatedItem);
      });
      
      // 只有在内容真正发生变化时才更新
      const hasChanged = !deepEqual(orderItems.value, updatedOrderItems);
      
      if (hasChanged) {
        orderItems.value = updatedOrderItems;
        
        // 如果購物車清空了，重置 orderInterval
        if (updatedOrderItems.length === 0) {
          orderInterval.value = "";
        }
        
        // Sync to server after stock sync changes
        updateTimestamp();
        debouncedSyncToServer();
      }
    };
    
    // make sure the cart is synced with other tabs
    if (import.meta.client) {
      window.addEventListener('storage', (event) => {
        if (event.key === 'cart' && event.newValue) {
          try {
            const newState = JSON.parse(event.newValue);
            if (newState && newState.lastModified) {
              // 使用 timestamp 比較，只有當新的 lastModified 更新時才同步
              if (isNewerThan(newState.lastModified, lastModified.value)) {
                // 更新所有狀態
                if (newState.orderItems) orderItems.value = newState.orderItems;
                if (newState.stock) stock.value = newState.stock;
                if (newState.orderInterval !== undefined) orderInterval.value = newState.orderInterval;
                lastModified.value = newState.lastModified;
                
                console.log('Cart synced from storage event - newer timestamp detected');
              }
              // 如果 timestamp 相同或更舊，則不做任何更新
            }
          } catch (e) {
            console.error('Failed to sync cart from storage event', e);
          }
        }
      });
    }

    return {
      orderItems,
      stock,
      getStockIssues,
      orderInterval,
      lastModified,
      addToCart,
      removeFromCart,
      getStock,
      resetStock,
      clearCart,
      emptyData,
      syncCartWithStock,
      // Timestamp utilities
      isNewerThan,
      updateTimestamp,
      // Expose sync state for external use
      get isSyncing() { return isSyncing; }
    };
  },
  {
    persist: import.meta.client ? {
      storage: localStorage,
    } : false,
  }
);
